import { useEffect, useMemo, useState } from "react"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { Box } from "@mui/material"

import { useApiQuery } from "../../../../../core/api/useApiQuery"

import {
    MeetingServicePriceListState,
    CreateOrderDialogFlow,
    CreateOrderDialogMode,
    CreateOrderDialogType,
    SelectedMainUserState,
    MeetingServicePriceListStatus,
} from "../../createOrderTypes"
import { useCreateOrderDialogType } from "../../hooks/useCreateOrderDialogType"
import { getMeetingServicePriceListStatusFlags } from "../../hooks/useMeetingServicePriceListStatus"
import { DeliveryTimesField, DynamicConfigKey, InputFieldView, MeetingServiceConfig } from "../form/formTypes"

import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { NoProductsDialog } from "./NoProductsDialog"
import { ShopListHeader } from "./header/ShopListHeader"
import { ShopListContent } from "./ShopListContent"
import { ShopListSkeleton } from "./ShopListSkeleton"
import { filterShops } from "./helpers"
import {
    fetchProductsForUser,
    FetchProductsForUserResponse,
    UserDetails,
} from "../../../../../modules/nonAdminUser/nonAdminUserApi"

const useStyles = makeStyles()(() => ({
    container: {
        display: "grid",
        maxHeight: "100%",
        gridTemplateRows: "auto 1fr",
        overflow: "hidden",
        width: "100%",
    },
}))
export type ShopFiltersTypes = Readonly<{
    isFavorite: boolean
    search: string
    isListView: boolean
}>

type ShopListProps = Readonly<{
    meetingServiceConfiguration?: MeetingServiceConfig
    handleCloseMainDialog: () => void
    flowConfig?: CreateOrderDialogFlow
    handleOpenSelectPriceListsDialog: () => void
    dialogType: CreateOrderDialogType
    user: SelectedMainUserState
    orderId?: number
    mode?: CreateOrderDialogMode
}>

const transformProductsData = (data: FetchProductsForUserResponse) => {
    return data.productGroups || []
}

export function ShopList({
    meetingServiceConfiguration,
    handleCloseMainDialog,
    flowConfig,
    handleOpenSelectPriceListsDialog,
    dialogType,
    user,
    orderId,
    mode,
}: ShopListProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const { userId, userType } = user
    const [{ value: priceListState }, , priceListHelpers] = useField<MeetingServicePriceListState>("priceList")
    const { item: priceList, status: priceListStatus } = priceListState
    const [{ value: deliveries }] = useField<DeliveryTimesField | undefined>(DynamicConfigKey.MULTIPLE_DELIVERIES)
    const { isRefundOrder } = useCreateOrderDialogType(dialogType)

    const productCatalogView = meetingServiceConfiguration?.sections
        .flatMap(({ groups }) => groups.flatMap(({ fields }) => fields))
        .find((field) => field.key === DynamicConfigKey.PRODUCT_CATALOG)?.inputField?.view

    const [filters, setFilters] = useState<ShopFiltersTypes>(() => ({
        isFavorite: false,
        search: "",
        isListView: productCatalogView === InputFieldView.LIST,
    }))
    const searchRegex = useMemo(
        () => (filters.search ? new RegExp(`(${filters.search})`, "i") : undefined),
        [filters.search],
    )

    // When making changes to this request, also need to update the parameters
    // for queryClient.getQueryData in the file MeetingServiceDelivery.tsx
    const {
        data: products = [],
        isLoading,
        isFetched,
        isError,
    } = useApiQuery(fetchProductsForUser, {
        queryName: UserDetails.USER_PRODUCTS_LIST,
        dependencies: {
            userId,
            merchantId: priceList?.merchantId!,
            priceListId: priceList?.priceListId!,
            deliveryDate: deliveries?.deliveryDate ?? dateTimeFactory.toDate(dateTimeFactory.now()),
            ...(mode === "edit" ? { mode } : {}),
        },
        enabled: Boolean(priceList),
        select: transformProductsData,
    })

    const filteredShops = useMemo(
        () => filterShops(filters.isFavorite, searchRegex, products),
        [products, filters.isFavorite, searchRegex],
    )

    useEffect(() => {
        const {
            isError: isPriceListError,
            isNoProducts,
            isReady,
            isUnselected,
            isValidating,
        } = getMeetingServicePriceListStatusFlags(priceListStatus)

        if (!priceList?.priceListId) {
            if (!isUnselected) {
                priceListHelpers.setValue({ ...priceListState, status: MeetingServicePriceListStatus.UNSELECTED })
            }
            return
        }

        if (isLoading) {
            if (!isValidating) {
                priceListHelpers.setValue({ ...priceListState, status: MeetingServicePriceListStatus.VALIDATING })
            }
            return
        }

        if (isError) {
            if (!isPriceListError) {
                priceListHelpers.setValue({ ...priceListState, status: MeetingServicePriceListStatus.ERROR })
            }
            return
        }

        if (!isLoading && isFetched && products.length === 0) {
            if (!isNoProducts) {
                priceListHelpers.setValue({ ...priceListState, status: MeetingServicePriceListStatus.NO_PRODUCTS })
            }
            return
        }

        if (!isReady) {
            priceListHelpers.setValue({ ...priceListState, status: MeetingServicePriceListStatus.READY })
        }
    }, [isError, isFetched, isLoading, priceList?.priceListId, priceListStatus, products.length])

    if (isLoading) {
        return <ShopListSkeleton />
    }

    const shouldShowNoProductsDialog =
        !isRefundOrder &&
        !isLoading &&
        isFetched &&
        getMeetingServicePriceListStatusFlags(priceListStatus).isNoProductsOrError

    if (shouldShowNoProductsDialog) {
        return (
            <NoProductsDialog
                handleCloseMainDialog={handleCloseMainDialog}
                handleOpenSelectPriceListsDialog={
                    !priceList?.isOnlyOnePriceList ? handleOpenSelectPriceListsDialog : undefined
                }
                isError={isError}
            />
        )
    }

    return (
        <Box className={classes.container}>
            {!isRefundOrder && (
                <ShopListHeader
                    filters={filters}
                    setFilters={setFilters}
                    meetingServiceConfiguration={meetingServiceConfiguration}
                    handleCloseMainDialog={handleCloseMainDialog}
                    flowConfig={flowConfig}
                    dialogType={dialogType}
                    userType={userType}
                />
            )}
            <ShopListContent
                productsList={filteredShops}
                filters={filters}
                searchRegex={searchRegex}
                orderId={orderId}
                isRefundOrder={isRefundOrder}
                handleCloseMainDialog={handleCloseMainDialog}
                userId={userId}
                isEditMode={mode === "edit"}
            />
        </Box>
    )
}
